import { custom } from 'devextreme/ui/dialog';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { apiFetch, readErrorMessage } from '../api/client';
import { mountApiBase } from './fileBrowserApi';
import { conflictChoiceToPolicy, resolveFileConflict, type OnConflictPolicy } from './fileConflictDialog';
import { parseItemPathForRoutes, toVirtualPath, type MountRoute } from './createFileBrowserProvider';

type FileManagerInstance = {
  option: (name: string, value: unknown) => void;
  refresh: () => Promise<unknown>;
};

export interface ZipRequest {
  destinationPath: string;
  archiveName: string;
  sources: string[];
}

export function buildZipRequest(
  items: readonly FileSystemItem[],
  routes: readonly MountRoute[],
  currentDirectoryPath: string
): { mountId: string; request: ZipRequest } | null {
  const parsedDir = parseItemPathForRoutes({ path: currentDirectoryPath, name: '' }, routes);
  if (!parsedDir || parsedDir.mount.mode !== 'ReadWrite') return null;

  const sources: string[] = [];
  for (const item of items) {
    const parsed = parseItemPathForRoutes(item, routes);
    if (!parsed?.relative) return null;
    sources.push(parsed.relative);
  }

  if (sources.length === 0) return null;

  return {
    mountId: parsedDir.mount.id,
    request: {
      destinationPath: parsedDir.relative,
      archiveName: defaultArchiveName(items),
      sources
    }
  };
}

export function normalizeArchiveName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Archive name is required.');
  return /\.zip$/i.test(trimmed) ? trimmed : `${trimmed}.zip`;
}

export function resolveZipArchiveVirtualPath(
  currentDirectoryPath: string,
  archiveName: string,
  routes: readonly MountRoute[]
): string | null {
  const parsedDir = parseItemPathForRoutes({ path: currentDirectoryPath, name: '' }, routes);
  if (!parsedDir) return null;

  const normalized = normalizeArchiveName(archiveName);
  const relative = parsedDir.relative ? `${parsedDir.relative}/${normalized}` : normalized;
  return toVirtualPath(parsedDir.mount.key, relative);
}

export async function focusZipArchiveAfterCreate(
  instance: FileManagerInstance,
  currentDirectoryPath: string,
  archiveName: string,
  routes: readonly MountRoute[]
): Promise<void> {
  const archiveVirtualPath = resolveZipArchiveVirtualPath(currentDirectoryPath, archiveName, routes);

  instance.option('selectedItemKeys', []);
  await instance.refresh();

  if (!archiveVirtualPath) return;

  instance.option('selectedItemKeys', [archiveVirtualPath]);
  instance.option('focusedItemKey', archiveVirtualPath);
}

export function defaultArchiveName(items: readonly FileSystemItem[]): string {
  if (items.length === 1) {
    const name = items[0]!.name;
    return name.endsWith('.zip') ? name : `${name}.zip`;
  }
  return 'Archive.zip';
}

export async function promptArchiveName(defaultName: string): Promise<string | null> {
  const safeDefault = escapeHtml(defaultName);
  const dialog = custom({
    title: 'Create zip archive',
    messageHtml: `<label style="display:block;margin-bottom:0.35rem;">Archive name</label><input type="text" class="jarvis-zip-name-input" value="${safeDefault}" style="width:100%;padding:0.35rem 0.5rem;" />`,
    buttons: [
      {
        text: 'Create',
        type: 'default',
        stylingMode: 'contained',
        onClick: () => {
          const input = document.querySelector<HTMLInputElement>('.jarvis-zip-name-input');
          return input?.value?.trim() || defaultName;
        }
      },
      {
        text: 'Cancel',
        stylingMode: 'text',
        onClick: () => null
      }
    ]
  });

  const result = await dialog.show();
  if (result === null || result === false) return null;
  return typeof result === 'string' ? result : defaultName;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function createZipArchive(
  mountId: string,
  request: ZipRequest,
  onConflict?: OnConflictPolicy
): Promise<void> {
  const res = await apiFetch(`${mountApiBase(mountId)}/zip`, {
    method: 'POST',
    body: JSON.stringify(onConflict ? { ...request, onConflict } : request)
  });

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { errorText?: string; conflict?: boolean };
    if (body.conflict) {
      const choice = await resolveFileConflict(request.archiveName);
      const policy = conflictChoiceToPolicy(choice);
      if (choice === 'skip') return;
      if (choice === 'cancel' || !policy) throw new Error('Zip cancelled.');
      return createZipArchive(mountId, request, policy);
    }
  }

  if (!res.ok) throw new Error(await readErrorMessage(res));
}
